import { getIn } from 'formik';
import moment from 'moment';

import { onboardingQuestionnaireActions } from '../../../../../../../store';
import { getAllFieldsTouched } from '../../../../../../common';
import { AGREEMENT_CONSENT_FIELD_NAMES, AGREEMENT_INITIAL_VALUES } from './constants';
import { AGREEMENT_FORM_SCHEMA } from '../validationSchema';
import { API_DATE_FORMAT } from '../../../../../../../constants';

import type { FormikProps, FormikTouched } from 'formik';
import type { Moment } from 'moment';
import type { AppDispatch } from '../../../../../../../store';
import type { GetAccessTokenSilently } from '../../../../../../../utils/types';
import type {
  AgreementConsentFieldName,
  AgreementSignatureFieldName,
  AgreementSignatureSavedFieldValues,
} from './types';
import type { AgreementFormValues } from '../../../../types';
import type {
  IOnboardingQuestionnaireAgreement,
  IOnboardingQuestionnaireConsentSignaturePayload,
} from '../types';

function getMomentValue(value: string | null | undefined): Moment | null {
  if (!value) {
    return null;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
}

export function isAgreementSignatureDateFieldValid(values: AgreementFormValues): boolean {
  const value = values.signature_date;
  return value == null || value.isValid();
}

export function getAgreementInitialValues(
  agreement: IOnboardingQuestionnaireAgreement | null | undefined,
  providerFullName = '',
): AgreementFormValues {
  const values =
    agreement == null
      ? { ...AGREEMENT_INITIAL_VALUES }
      : {
          consent_electronic_delivery_via_email:
            agreement.consent_electronic_delivery_via_email ?? false,
          consent_text_messages_regarding_aba_services:
            agreement.consent_text_messages_regarding_aba_services ?? false,
          consent_electronic_signature_platform:
            agreement.consent_electronic_signature_platform ?? false,
          consent_complete_required_assessments:
            agreement.consent_complete_required_assessments ?? false,
          consent_paper_document_delivery_notice:
            agreement.consent_paper_document_delivery_notice ?? false,
          consent_electronic_delivery_freely_given:
            agreement.consent_electronic_delivery_freely_given ?? false,
          printed_name: agreement.printed_name ?? '',
          signature: agreement.signature ?? '',
          signature_date: agreement.signature_date
            ? getMomentValue(agreement.signature_date)
            : null,
        };

  // Keep the default date stable across repeated calls on the same day.
  return {
    ...values,
    printed_name: values.printed_name || providerFullName,
    signature_date: values.signature_date ?? getMomentValue(moment().format(API_DATE_FORMAT)),
  };
}

export async function isAgreementSignatureFieldSchemaValid(
  name: AgreementSignatureFieldName,
  values: AgreementFormValues,
): Promise<boolean> {
  try {
    await AGREEMENT_FORM_SCHEMA.validateAt(name, values);
    return true;
  } catch {
    return false;
  }
}

export function getAgreementSignatureSavedValues(
  values: AgreementFormValues,
): AgreementSignatureSavedFieldValues {
  return {
    printed_name: values.printed_name,
    signature: values.signature,
    signature_date: values.signature_date?.format(API_DATE_FORMAT) ?? '',
  };
}

export function isAgreementSignatureFieldUnchanged(
  name: AgreementSignatureFieldName,
  values: AgreementFormValues,
  saved: AgreementSignatureSavedFieldValues,
): boolean {
  return saved[name] === getAgreementSignatureSavedValues(values)[name];
}

export async function persistAgreementSignatureFieldSave({
  previousSave,
  dispatch,
  onboardingQuestionnaireId,
  values,
  getAccessTokenSilently,
  formik,
}: {
  previousSave: Promise<void>;
  dispatch: AppDispatch;
  onboardingQuestionnaireId: string;
  values: AgreementFormValues;
  getAccessTokenSilently: GetAccessTokenSilently;
  formik: FormikProps<AgreementFormValues>;
}): Promise<AgreementSignatureSavedFieldValues | null> {
  try {
    await previousSave;
  } catch {
    // Swallow prior save errors so this save still proceeds.
  }

  const resultAction = await dispatch(
    onboardingQuestionnaireActions.updateOnboardingQuestionnaireAgreement({
      onboardingQuestionnaireId,
      data: getAgreementDocumentPayload(values),
      getAccessTokenSilently,
    }),
  );

  if (
    !onboardingQuestionnaireActions.updateOnboardingQuestionnaireAgreement.fulfilled.match(
      resultAction,
    )
  ) {
    return null;
  }

  const errors = await formik.validateForm(values);
  formik.setErrors(errors);
  formik.setTouched(getAllFieldsTouched(values), false);
  return getAgreementSignatureSavedValues(values);
}

export function getAgreementConsentSavedValues(values: AgreementFormValues) {
  return {
    consent_electronic_delivery_via_email: values.consent_electronic_delivery_via_email,
    consent_text_messages_regarding_aba_services:
      values.consent_text_messages_regarding_aba_services,
    consent_electronic_signature_platform: values.consent_electronic_signature_platform,
    consent_complete_required_assessments: values.consent_complete_required_assessments,
    consent_paper_document_delivery_notice: values.consent_paper_document_delivery_notice,
    consent_electronic_delivery_freely_given: values.consent_electronic_delivery_freely_given,
  };
}

export function getAgreementConsentTouchedState(
  values: AgreementFormValues,
  currentTouched: FormikTouched<AgreementFormValues>,
): FormikTouched<AgreementFormValues> {
  return AGREEMENT_CONSENT_FIELD_NAMES.reduce<FormikTouched<AgreementFormValues>>(
    (touched, fieldName) => ({
      ...touched,
      [fieldName]: values[fieldName] === false ? true : Boolean(getIn(currentTouched, fieldName)),
    }),
    { ...currentTouched },
  );
}

export function isAgreementConsentFieldName(
  name: Extract<keyof AgreementFormValues, string>,
): name is AgreementConsentFieldName {
  return AGREEMENT_CONSENT_FIELD_NAMES.some((consentFieldName) => name === consentFieldName);
}

export function getAgreementDocumentPayload(
  values: AgreementFormValues,
): IOnboardingQuestionnaireConsentSignaturePayload {
  return {
    consent_electronic_delivery_via_email: values.consent_electronic_delivery_via_email,
    consent_text_messages_regarding_aba_services:
      values.consent_text_messages_regarding_aba_services,
    consent_electronic_signature_platform: values.consent_electronic_signature_platform,
    consent_complete_required_assessments: values.consent_complete_required_assessments,
    consent_paper_document_delivery_notice: values.consent_paper_document_delivery_notice,
    consent_electronic_delivery_freely_given: values.consent_electronic_delivery_freely_given,
    printed_name: values.printed_name || null,
    signature: values.signature || null,
    signature_date: values.signature_date?.format(API_DATE_FORMAT) ?? null,
  };
}
